import { BrainActionSummary } from "../clientBrainActions/BrainActionSummary";
import { DataQuerySummary } from "../workflowData/DataQuerySummary";
import { StatisticsSummary } from "../workflowData/StatisticsSummary";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useMsal } from "@azure/msal-react";
import { ArrowLeft, Eye, LayoutPanelTop, Plus, Save, Send, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import { getToken, useMsalAuth } from "../../authConfig";
import { getOrganizationMembers } from "../../api/organizationAccessApi";
import { useOrganization } from "../../contexts/OrganizationContext";
import { Button, Modal, Textarea } from "../../components/ui";
import { NoteWorkflowsCanvas } from "../../components/NoteWorkflows/NoteWorkflowsCanvas";
import { WorkflowNodeDialog } from "../../components/NoteWorkflows/WorkflowNodeDialog";
import { selectedTaskSettings } from "../../components/NoteWorkflows/boxTaskTypes";
import type { BoxTaskSettings } from "../../components/NoteWorkflows/boxTaskTypes";
import type { NoteWorkflow, NoteWorkflowsDocument, WorkflowNodeSelection } from "../../components/NoteWorkflows/noteWorkflowsTypes";
import type { OrganizationMemberDirectory } from "../../pages/organization-admin/types";
import { WorkflowPreviewCard } from "../workflowBuilder/WorkflowPreviewCard";
import { BuilderWorkspace } from "../workflowBuilder/WorkflowBuilderPage";
import type { WorkflowDraft } from "../workflowBuilder/models";
import { DataScopePicker } from "../workflowData/DataScopePicker";
import { hasDataScope, scopeKey, scopeProjectId, type DataScope } from "../workflowData/models";
import { ProjectSourceBadges } from "../workflowBuilder/ProjectSourceBadges";
import { usesProjectSources } from "../workflowBuilder/builderPreviewContext";
import { WorkflowBuilderApiError } from "../workflowBuilder/response";
import { useBuilderIdentity } from "../workflowBuilder/useBuilderIdentity";
import builderStyles from "../workflowBuilder/WorkflowBuilder.module.css";
import { previewOrganizationSelection, proposeOrganizationWorkflow, saveOrganizationWorkflow } from "./api";
import { organizationWorkflowSchema } from "./models";
import type { OrganizationPreview, OrganizationPreviewBox, PendingSignPermission } from "./models";
import { currentOrganizationPreview, visibleOrganizationMessages } from "./previewContext";
import type { ScopedOrganizationChatMessage } from "./previewContext";
import { documentationAuthoringWorkflow, editNativeNode, hasWorkflowChanges, NativeWorkflowNeedsOutputError, normalizeNativeWorkflow, outputNodes, pendingAfterPermissionEdit, previewDisplayWorkflow, removeNativeNode, retainPendingNodes, selectedNativeNode, workflowDocument } from "./nativeWorkflow";
import styles from "./AdminWorkflowChatWindow.module.css";
import { ShiftHandoverEditor } from "./ShiftHandoverEditor";
import type { ShiftHandoverDefinition } from "./handoverModels";
import type { OrganizationChatMessage } from "./models";

type Props = {
    initialWorkflow?: NoteWorkflow;
    initialScheduledDraftId?: string;
    initialHandoverId?: string;
    onHandoverSaved?: () => void;
    onClose: () => void;
    onScheduledClose?: () => void;
    onSaved: (document: NoteWorkflowsDocument) => void;
};
type ChoiceReview = { boxId: string; value: string } & (
    { status: "loading" } | { status: "ready"; box: OrganizationPreviewBox } | { status: "error"; error: string }
);

export function AdminWorkflowChatWindow(props: Props) {
    const { context, switching, isOrganizationAdmin } = useOrganization();
    const { scope } = useBuilderIdentity();
    const organizationId = context?.currentOrganizationId;
    // A change of organization unmounts the proposal and cancels its pending requests.
    if (!organizationId || switching || !isOrganizationAdmin) return null;
    return <AdminWorkflowChatController key={`${scope}:${organizationId}:${props.initialHandoverId ?? props.initialScheduledDraftId ?? props.initialWorkflow?.id ?? "new"}`} organizationId={organizationId} {...props} />;
}

function AdminWorkflowChatController({ organizationId, initialScheduledDraftId, onScheduledClose, ...props }: Props & { organizationId: string }) {
    const { t } = useTranslation();
    const [scheduled, setScheduled] = useState<{ id: string; draft?: WorkflowDraft } | null>(() => initialScheduledDraftId ? { id: initialScheduledDraftId } : null);
    const [startFresh, setStartFresh] = useState(false);
    const [handover, setHandover] = useState<{ definition: ShiftHandoverDefinition; messages: OrganizationChatMessage[] } | null>(null);
    if (handover || props.initialHandoverId) return <ShiftHandoverEditor organizationId={organizationId}
        initialRuleId={props.initialHandoverId} initialDefinition={handover?.definition} initialMessages={handover?.messages}
        onClose={props.onClose} onSaved={props.onHandoverSaved ?? props.onClose} />;
    if (!scheduled) return <AdminWorkflowChatSession {...props} initialWorkflow={startFresh ? undefined : props.initialWorkflow}
        organizationId={organizationId} onScheduledDraft={draft => setScheduled({ id: draft.id, draft })}
        onHandover={(definition, history) => setHandover({ definition, messages: history })} />;
    const close = onScheduledClose ?? props.onClose;
    return <Modal open size="fullscreen" className={builderStyles.editorWindow} onOpenChange={open => { if (!open) close(); }}>
        <Dialog.Title className={builderStyles.accessibleTitle}>{t("organizationWorkflowBuilder.scheduledTitle", { defaultValue: "My scheduled tasks" })}</Dialog.Title>
        <Dialog.Description className={builderStyles.accessibleTitle}>{t("organizationWorkflowBuilder.scheduledHelp", { defaultValue: "These tasks and their Workspace results are visible only to you. Save to activate the schedule." })}</Dialog.Description>
        <Button className={builderStyles.editorClose} variant="ghost" iconOnly aria-label={t("settingsPage.close")} onClick={close}><X size={20} /></Button>
        <BuilderWorkspace key={scheduled.id} draftId={scheduled.id} initialDraft={scheduled.draft} organizationId={organizationId} switching={false}
            onClose={close} onCreate={() => { setStartFresh(true); setScheduled(null); }}
            onDraftCreated={draft => setScheduled({ id: draft.id, draft })} />
    </Modal>;
}

function AdminWorkflowChatSession({ organizationId, initialWorkflow, onClose, onSaved, onScheduledDraft, onHandover }: Props & { organizationId: string; onScheduledDraft: (draft: WorkflowDraft) => void;
    onHandover: (definition: ShiftHandoverDefinition, messages: OrganizationChatMessage[]) => void }) {
    const { t, i18n } = useTranslation();
    const { instance } = useMsal();
    const resolveToken = useCallback(() => useMsalAuth ? getToken(instance) : Promise.resolve(undefined), [instance]);
    const [workflow, setWorkflow] = useState<NoteWorkflow | null>(() => initialWorkflow ? documentationAuthoringWorkflow(initialWorkflow) : null);
    // Keep the original separately: Save replaces exactly this persisted workflow.
    const [baseWorkflow, setBaseWorkflow] = useState<NoteWorkflow | null>(() => initialWorkflow ? normalizeNativeWorkflow(initialWorkflow) : null);
    const [messages, setMessages] = useState<ScopedOrganizationChatMessage[]>([]);
    const [preview, setPreview] = useState<OrganizationPreview | null>(null);
    const [selectedScope, setSelectedScope] = useState<DataScope | null>(null);
    const [pendingPermissions, setPendingPermissions] = useState<PendingSignPermission[]>([]);
    const [mode, setMode] = useState<"build" | "preview">("build");
    const [text, setText] = useState("");
    const [pendingText, setPendingText] = useState("");
    const [busy, setBusy] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [selected, setSelected] = useState<WorkflowNodeSelection | null>(null);
    const [directory, setDirectory] = useState<OrganizationMemberDirectory | null>(null);
    const [membersLoading, setMembersLoading] = useState(false);
    const [membersError, setMembersError] = useState<string | null>(null);
    const [discardAction, setDiscardAction] = useState<"close" | "new" | null>(null);
    const [choiceReview, setChoiceReview] = useState<ChoiceReview | null>(null);
    const choiceRequest = useRef<AbortController | null>(null);
    const request = useRef<AbortController | null>(null);
    const pendingChat = useRef<{ fingerprint: string; requestId: string } | null>(null);
    const inFlight = useRef(false);
    const alive = useRef(true);
    const contextEpoch = useRef(0);
    const membersAttempt = useRef(0);
    const composer = useRef<HTMLTextAreaElement>(null);
    const scrollEnd = useRef<HTMLDivElement>(null);
    const previewCard = useRef<HTMLElement>(null);
    const discardTrigger = useRef<HTMLElement | null>(null);
    const locked = busy || saving || choiceReview !== null;
    const document = useMemo(() => workflow ? workflowDocument(workflow) : null, [workflow]);
    const node = selectedNativeNode(workflow, selected);
    const outputNode = workflow && selected?.nodeKind !== "trigger" ? outputNodes(workflow).find(item => item.id === selected?.nodeId) : undefined;
    const nodeSources = workflow && selected?.nodeKind !== "trigger"
        ? outputNodes(workflow).find(item => item.id === selected?.nodeId)?.context_selectors ?? [] : [];
    const taskSettings = document ? selectedTaskSettings(document, selected) : undefined;
    const displayWorkflow = useMemo(() => workflow ? previewDisplayWorkflow(workflow) : null, [workflow]);
    const requiresProject = usesProjectSources(displayWorkflow ?? undefined);
    const activeScope = requiresProject ? selectedScope : null;
    const activeProjectId = scopeProjectId(activeScope);
    const currentPreview = currentOrganizationPreview(preview, activeProjectId, requiresProject, activeScope);
    const visibleMessages = visibleOrganizationMessages(messages, activeProjectId, activeScope);
    const canvasTopology = workflow ? JSON.stringify([workflow.id, workflow.trigger.id, ...outputNodes(workflow).map(item => item.id)]) : "empty";
    const hasBoxes = Boolean(workflow && outputNodes(workflow).length);
    const dirty = hasWorkflowChanges(workflow, baseWorkflow ? documentationAuthoringWorkflow(baseWorkflow) : null) || Boolean(messages.length || text.trim());
    const canSave = Boolean(workflow && hasBoxes && !pendingPermissions.length && !locked && !selected);

    useEffect(() => {
        alive.current = true;
        return () => { alive.current = false; request.current?.abort(); choiceRequest.current?.abort(); membersAttempt.current += 1; };
    }, []);

    const loadMembers = useCallback(async () => {
        const attempt = ++membersAttempt.current;
        setDirectory(null); setMembersError(null); setMembersLoading(true);
        try {
            const result = await getOrganizationMembers(organizationId, await resolveToken());
            if (alive.current && attempt === membersAttempt.current) {
                if (result.organization.id !== organizationId) throw new Error(t("organizationWorkflowBuilder.membersError"));
                setDirectory(result);
            }
        } catch (caught: unknown) {
            if (alive.current && attempt === membersAttempt.current) {
                setDirectory(null);
                setMembersError(caught instanceof Error ? caught.message : t("organizationWorkflowBuilder.membersError"));
            }
        } finally {
            if (alive.current && attempt === membersAttempt.current) setMembersLoading(false);
        }
    }, [organizationId, resolveToken, t]);
    useEffect(() => { void loadMembers(); }, [loadMembers]);

    useEffect(() => {
        if (mode === "preview" && preview && !pendingText) previewCard.current?.scrollIntoView({ block: "start", behavior: "smooth" });
        else scrollEnd.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }, [messages.length, pendingText, mode, preview]);

    const resetProposal = () => {
        contextEpoch.current += 1;
        request.current?.abort(); choiceRequest.current?.abort();
        inFlight.current = false;
        setWorkflow(null); setBaseWorkflow(null); setMessages([]); setPreview(null); setPendingPermissions([]);
        setText(""); setError(""); setMode("build"); setSelected(null); setSelectedScope(null);
        composer.current?.focus();
    };
    const finishDiscard = (action: "close" | "new") => {
        setDiscardAction(null);
        if (action === "close") { request.current?.abort(); onClose(); }
        else resetProposal();
    };
    const requestDiscard = (action: "close" | "new") => {
        if (!dirty) { finishDiscard(action); return; }
        const activeElement = window.document.activeElement;
        discardTrigger.current = activeElement instanceof HTMLElement ? activeElement : null;
        setDiscardAction(action);
    };
    const close = () => { if (!saving && !selected && !discardAction && !choiceReview) requestDiscard("close"); };
    const startNew = () => { if (!locked) requestDiscard("new"); };

    const closeChoice = () => { choiceRequest.current?.abort(); setChoiceReview(null); };
    const selectScope = (dataScope: DataScope | null) => {
        if (scopeKey(dataScope) === scopeKey(selectedScope)) return;
        contextEpoch.current += 1;
        request.current?.abort(); choiceRequest.current?.abort();
        request.current = null; choiceRequest.current = null; inFlight.current = false;
        setSelectedScope(dataScope);
        setPreview(null); setChoiceReview(null);
        setMessages(current => current.filter(message => message.projectId === null && !message.dataScope));
        setBusy(false); setPendingText(""); setError("");
    };
    const clearProjectResults = () => {
        setPreview(null);
        setMessages(current => current.filter(message => message.projectId === null && !message.dataScope));
    };
    const changePreviewSelection = async (boxId: string, value: string) => {
        if (!workflow || !currentPreview || busy || saving) return;
        choiceRequest.current?.abort();
        const controller = new AbortController();
        const epoch = contextEpoch.current;
        choiceRequest.current = controller;
        setChoiceReview({ boxId, value, status: "loading" });
        let authenticated = false;
        try {
            const token = await resolveToken();
            authenticated = true;
            if (controller.signal.aborted || !alive.current || epoch !== contextEpoch.current) return;
            const box = await previewOrganizationSelection(organizationId, workflow, currentPreview.input, boxId, value, i18n.language, token, controller.signal, activeProjectId, activeScope);
            if (!controller.signal.aborted && alive.current && epoch === contextEpoch.current) setChoiceReview({ boxId, value, status: "ready", box });
        } catch (caught: unknown) {
            if (!controller.signal.aborted && alive.current && epoch === contextEpoch.current) {
                if (!authenticated || (caught instanceof WorkflowBuilderApiError && [401, 403, 404].includes(caught.status))) clearProjectResults();
                setChoiceReview({ boxId, value, status: "error", error: caught instanceof Error ? caught.message : t("workflowBuilder.sendError") });
            }
        }
    };

    const send = async () => {
        const message = text.trim();
        if (locked || inFlight.current || selected || !message) return;
        if (mode === "preview" && requiresProject && !hasDataScope(activeScope, activeProjectId)) {
            setError(t("workflowData.choose")); return;
        }
        const controller = new AbortController();
        const epoch = contextEpoch.current;
        request.current = controller; inFlight.current = true;
        setBusy(true); setPendingText(message); setError("");
        let authenticated = false;
        try {
            const token = await resolveToken();
            authenticated = true;
            if (controller.signal.aborted || !alive.current || epoch !== contextEpoch.current) return;
            const fingerprint = JSON.stringify([workflow, message, visibleMessages, mode, pendingPermissions, activeProjectId, activeScope]);
            if (pendingChat.current?.fingerprint !== fingerprint) pendingChat.current = { fingerprint, requestId: crypto.randomUUID() };
            const result = await proposeOrganizationWorkflow(organizationId, {
                workflow, message, messages: visibleMessages, mode, language: i18n.language,
                request_id: pendingChat.current.requestId, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                pending_sign_permissions: pendingPermissions, project_id: activeProjectId, data_scope: activeScope
            }, token, controller.signal);
            if (controller.signal.aborted || !alive.current || epoch !== contextEpoch.current) return;
            pendingChat.current = null;
            if (result.scheduled_draft) { onScheduledDraft(result.scheduled_draft); return; }
            if (result.handover) { onHandover(result.handover, [...visibleMessages, { role: "user", content: message },
                ...(result.reply ? [{ role: "assistant" as const, content: result.reply }] : [])]); return; }
            setWorkflow(result.workflow ? documentationAuthoringWorkflow(result.workflow) : null);
            setPreview(result.preview); setPendingPermissions(result.pending_sign_permissions);
            setMessages(current => [...current, { role: "user", content: message, projectId: activeProjectId, dataScope: activeScope },
                ...(result.reply.trim() ? [{ role: "assistant" as const, content: result.reply, projectId: activeProjectId, dataScope: activeScope }] : [])]);
            setText("");
            if (result.intent === "preview") setMode("preview");
        } catch (caught: unknown) {
            if (!controller.signal.aborted && alive.current && epoch === contextEpoch.current) {
                if (!authenticated || (caught instanceof WorkflowBuilderApiError && [401, 403, 404].includes(caught.status))) clearProjectResults();
                setError(caught instanceof Error ? caught.message : t("workflowBuilder.sendError"));
            }
        } finally {
            if (request.current === controller) request.current = null;
            if (!controller.signal.aborted && alive.current && epoch === contextEpoch.current) {
                inFlight.current = false; setBusy(false); setPendingText(""); composer.current?.focus();
            }
        }
    };

    const save = async () => {
        if (!workflow || !canSave || inFlight.current) return;
        const parsed = organizationWorkflowSchema.safeParse(workflow);
        if (!parsed.success) { setError(parsed.error.issues[0]?.message ?? t("organizationWorkflowBuilder.invalidWorkflow")); return; }
        const controller = new AbortController();
        request.current = controller; inFlight.current = true;
        setSaving(true); setError("");
        try {
            const token = await resolveToken();
            if (controller.signal.aborted || !alive.current) return;
            // Save the native proposal, never the preview's display projection.
            const result = await saveOrganizationWorkflow(organizationId, parsed.data, token, controller.signal, baseWorkflow);
            if (!controller.signal.aborted && alive.current) onSaved(result);
        } catch (caught: unknown) {
            if (!controller.signal.aborted && alive.current) setError(caught instanceof Error ? caught.message : t("organizationWorkflowBuilder.saveError"));
        } finally {
            if (request.current === controller) request.current = null;
            if (!controller.signal.aborted && alive.current) { inFlight.current = false; setSaving(false); }
        }
    };

    const saveNode = (title: string, description: string, permissions?: BoxTaskSettings) => {
        if (!workflow || !selected || locked) return;
        setWorkflow(editNativeNode(workflow, selected, { title, description, task_settings: permissions }));
        setPendingPermissions(current => pendingAfterPermissionEdit(current, selected.nodeId, taskSettings, permissions));
        setPreview(null); setError("");
    };
    const deleteSelectedNode = () => {
        if (!workflow || !selected || locked) return;
        if (selected.nodeKind === "trigger") { startNew(); return; }
        try {
            const next = removeNativeNode(workflow, selected);
            setWorkflow(next); setPendingPermissions(current => retainPendingNodes(current, next));
            setPreview(null); setError("");
        } catch (caught: unknown) {
            setError(caught instanceof NativeWorkflowNeedsOutputError ? t("organizationWorkflowBuilder.lastBoxRequired")
                : caught instanceof Error ? caught.message : t("organizationWorkflowBuilder.invalidWorkflow"));
        }
    };

    return <Modal open size="fullscreen" className={builderStyles.editorWindow} onOpenChange={open => { if (!open) close(); }}>
        <Dialog.Title className={builderStyles.accessibleTitle}>{t(baseWorkflow ? "organizationWorkflowBuilder.editTitle" : "organizationWorkflowBuilder.title")}</Dialog.Title>
        <Dialog.Description className={builderStyles.accessibleTitle}>{t(baseWorkflow ? "organizationWorkflowBuilder.editDescription" : "organizationWorkflowBuilder.description")}</Dialog.Description>
        <Button className={builderStyles.editorClose} variant="ghost" iconOnly disabled={saving} aria-label={t("settingsPage.close")} onClick={close}><X size={20} /></Button>
        <main className={builderStyles.page}>
            <Button className={builderStyles.backButton} variant="ghost" size="sm" iconOnly disabled={saving || Boolean(selected)}
                aria-label={t("workflowBuilder.backToLibrary")} onClick={close}><ArrowLeft size={20} /></Button>
            <section className={builderStyles.board} aria-label={t("workflowBuilder.boardTitle")} aria-busy={locked}>
                <div className={builderStyles.boardHeader}>
                    {requiresProject && <DataScopePicker value={selectedScope} disabled={saving || Boolean(selected)} onChange={selectScope} previewOnly />}
                    <div className={builderStyles.boardActions}>
                        <div className={builderStyles.modeSwitch} role="group" aria-label={t("workflowBuilder.mode")}>
                            <Button size="sm" variant={mode === "build" ? "primary" : "ghost"} aria-pressed={mode === "build"} disabled={locked}
                                leftIcon={<LayoutPanelTop size={15} />} onClick={() => setMode("build")}>{t("workflowBuilder.build")}</Button>
                            <Button size="sm" variant={mode === "preview" ? "primary" : "ghost"} aria-pressed={mode === "preview"} disabled={locked || !hasBoxes}
                                leftIcon={<Eye size={15} />} onClick={() => { setMode("preview"); composer.current?.focus(); }}>{t("workflowBuilder.preview")}</Button>
                        </div>
                        <Button variant="ghost" size="sm" disabled={!canSave} isLoading={saving} leftIcon={<Save size={16} />} onClick={() => { void save(); }}>
                            {t(saving ? "workflowBuilder.saving" : "workflowBuilder.save")}
                        </Button>
                        <Button variant="ghost" size="sm" disabled={locked} leftIcon={<Plus size={16} />} onClick={startNew}>{t("workflowBuilder.new")}</Button>
                    </div>
                </div>
                {document ? <div className={builderStyles.canvas}>
                    <NoteWorkflowsCanvas key={canvasTopology} document={document} selected={selected} variant="fullscreen" activateOnKeyboard
                        onNodeSelect={selection => { if (!locked) { setSelected(selection); void loadMembers(); } }} />
                </div> : <p className={builderStyles.emptyBoard}>{t("workflowBuilder.boardEmpty")}</p>}
            </section>
            <div className={builderStyles.conversation}>
                {visibleMessages.map((message, index) => <article key={index} className={message.role === "user" ? builderStyles.userMessage : builderStyles.assistantMessage}
                    aria-label={t(message.role === "user" ? "workflowBuilder.you" : "workflowBuilder.assistant")}>
                    <ReactMarkdown skipHtml>{message.content}</ReactMarkdown>
                </article>)}
                {pendingPermissions.length > 0 && <p role="status" className={styles.permissionNotice}>{t("organizationWorkflowBuilder.pendingPermissions")}</p>}
                <div hidden={mode !== "preview"}>
                    {currentPreview && displayWorkflow && <WorkflowPreviewCard key={activeProjectId ?? "sample"} workflow={displayWorkflow} preview={currentPreview} previewRef={previewCard}
                        disabled={!canSave || locked} saving={saving} saveSucceeded={false} error={error}
                        onSaveWorkflow={() => { void save(); }} onSelectionChange={(boxId, value) => { void changePreviewSelection(boxId, value); }} />}
                </div>
                {mode === "preview" && <p className={builderStyles.previewHint}>{t(requiresProject ? "workflowBuilder.projectPreviewHelp" : "organizationWorkflowBuilder.samplePreview")}</p>}
                {pendingText && <article className={builderStyles.userMessage}>{pendingText}</article>}
                {busy && <p role="status" className={builderStyles.working}>{t(mode === "preview" ? "workflowBuilder.running" : "workflowBuilder.thinking")}</p>}
                {saving && <p role="status" className={builderStyles.working}>{t("workflowBuilder.saving")}</p>}
                {error && <p role="alert" className={builderStyles.error}>{error}</p>}
                <div ref={scrollEnd} />
            </div>
            <form className={builderStyles.composer} onSubmit={event => { event.preventDefault(); void send(); }}>
                <Textarea ref={composer} value={text} onChange={event => setText(event.target.value)} disabled={locked} maxLength={16000} rows={3}
                    aria-label={t(mode === "preview" ? "workflowBuilder.previewInput" : "workflowBuilder.buildInput")}
                    placeholder={t(mode === "preview" ? requiresProject ? "workflowBuilder.projectPreviewPlaceholder" : "workflowBuilder.previewPlaceholder" : "workflowBuilder.buildPlaceholder")}
                    onKeyDown={event => { if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) { event.preventDefault(); void send(); } }} />
                <div className={builderStyles.composerFooter}><Button type="submit" variant="primary" disabled={locked || !text.trim()} isLoading={busy} leftIcon={<Send size={16} />}>{t("workflowBuilder.send")}</Button></div>
            </form>
        </main>
        {selected && node && <WorkflowNodeDialog open selection={selected} title={node.title ?? ""} description={node.description}
            onOpenChange={open => { if (!open) setSelected(null); }} taskSettings={taskSettings}
            directory={directory} membersLoading={membersLoading} membersError={membersError} onRetryMembers={() => { void loadMembers(); }}
            onSave={saveNode} onDelete={deleteSelectedNode}>
            <ProjectSourceBadges selectors={nodeSources} />
            <StatisticsSummary query={outputNode?.analytics} />
            <DataQuerySummary query={workflow?.data_query} timezone={workflow?.timezone} />
            <BrainActionSummary action={outputNode?.client_brain_action} />
        </WorkflowNodeDialog>}
        {choiceReview && <Modal open size="md" className={styles.discardDialog} overlayClassName={styles.discardOverlay}
            onOpenChange={open => { if (!open) closeChoice(); }}>
            <Modal.Header title={t("documentFlow.choice.review")}
                description={t("documentFlow.choice.previewNotice")} />
            <Modal.Body>
                {choiceReview.status === "loading" && <p role="status">{t("documentFlow.choice.generating")}</p>}
                {choiceReview.status === "error" && <p role="alert">{choiceReview.error}</p>}
                {choiceReview.status === "ready" && <p className={builderStyles.plainText}>{choiceReview.box.content}</p>}
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={closeChoice}>{t("settingsPage.account.cancel")}</Button>
                {choiceReview.status === "error" && <Button onClick={() => { void changePreviewSelection(choiceReview.boxId, choiceReview.value); }}>
                    {t("documentFlow.ibic.retry", { defaultValue: "Try again" })}</Button>}
                <Button disabled={choiceReview.status !== "ready"} onClick={() => {
                    if (choiceReview.status !== "ready") return;
                    const changedBox = choiceReview.box;
                    setPreview(current => current ? { ...current, boxes: current.boxes.map(box => box.box_id === changedBox.box_id ? changedBox : box) } : null);
                    closeChoice();
                }}>{t("documentFlow.choice.confirm")}</Button>
            </Modal.Footer>
        </Modal>}
        {discardAction && <Modal open size="md" className={styles.discardDialog} overlayClassName={styles.discardOverlay}
            onOpenChange={open => { if (!open) setDiscardAction(null); }}
            onCloseAutoFocus={event => {
                event.preventDefault();
                (discardTrigger.current?.isConnected ? discardTrigger.current : composer.current)?.focus();
            }}>
            <Modal.Header title={t("organizationWorkflowBuilder.discardTitle")} description={t("organizationWorkflowBuilder.discard")} />
            <Modal.Footer>
                <Button onClick={() => setDiscardAction(null)}>{t("settingsPage.account.cancel")}</Button>
                <Button variant="primary" onClick={() => { if (discardAction) finishDiscard(discardAction); }}>{t("organizationWorkflowBuilder.discardConfirm")}</Button>
            </Modal.Footer>
        </Modal>}
    </Modal>;
}
